/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk } from "@reduxjs/toolkit";
import {
    getAllFeedbackDetailsApi,
    getCourseFeedbackWithAnswersApi,
    getGradeSubjectFeedbackWithAnswersApi,
    getStudentFeedbackDetailsApi,
    getSubjectFeedbackForGradeWithAnswersApi,
    getSubjectFeedbackWithAnswersApi,
    submitGradeSubjectFeedbackApi,
    submitStudentFeedbackApi,
    submitSubjectFeedbackForGradeApi,
} from "@/api/reportingApi";
import { NonTermViewTypeEnum } from "@/ts/enums/NonTermViewTypeEnum";
import {
    AllFeedbackDetailsApiInterface,
} from "@/ts/interfaces/Reporting/SubjectFeedbackInterface";
import {
    FeedbackFormData,
    StudentFeedbackDetailsResponse,
} from "@/ts/interfaces/Reporting/api/StudentFeedbackApiDetailInterface";
import { GradeSubjectFeedbackApiInterface, GradeSubjectFeedbackFormData } from "@/ts/interfaces/Reporting/api/GradeSubjectFeedbackApiInterface";
import { SubjectFeedbackForGradeApiInterface, SubjectFeedbackForGradeFormData } from "@/ts/interfaces/Reporting/api/SubjectFeedbackForGradeApiInterface";

export const getStudentFeedbackDetailsThunk = createAsyncThunk<
    StudentFeedbackDetailsResponse,
    { studentId: number; familyGradeId: number; term: number; type: NonTermViewTypeEnum },
    { rejectValue: string }
>("reporting/feedbackDetails", async ({ studentId, familyGradeId, term, type }, { rejectWithValue }) => {
    try {
        const response = await getStudentFeedbackDetailsApi(studentId, familyGradeId, term, type);
        return response.data;
    } catch (err: any) {
        return rejectWithValue(err?.response?.data?.message || "Failed to load feedback details.");
    }
});

export const getGradeSubjcetFeedbackDetailsThunk = createAsyncThunk<
    StudentFeedbackDetailsResponse,
    { studentId: number; familyGradeId: number; type: NonTermViewTypeEnum },
    { rejectValue: string }
>("reporting/feedbackDetails", async ({ studentId, familyGradeId, type }, { rejectWithValue }) => {
    try {
        const response = await getStudentFeedbackDetailsApi(studentId, familyGradeId, null, type);
        return response.data;
    } catch (err: any) {
        return rejectWithValue(err?.response?.data?.message || "Failed to load feedback details.");
    }
});

export const getSubjectFeedbackWithAnswersThunk = createAsyncThunk<
    StudentFeedbackDetailsResponse,
    { studentId: number; familyGradeId: number; subjectId: number; term: number; type: NonTermViewTypeEnum; personalLearningPlanId?: number | null },
    { rejectValue: string }
>("reporting/subjectFeedbackWithAnswers", async ({ studentId, familyGradeId, subjectId, term, type, personalLearningPlanId }, { rejectWithValue }) => {
    try {
        const response = await getSubjectFeedbackWithAnswersApi(studentId, familyGradeId, subjectId, term, type, personalLearningPlanId);
        return response.data;
    } catch (err: any) {
        return rejectWithValue(err?.response?.data?.message || "Failed to load subject feedback.");
    }
});

export const getCourseFeedbackWithAnswersThunk = createAsyncThunk<
    StudentFeedbackDetailsResponse,
    { studentId: number; familyGradeId: number; term: number | null; courseId: number; type: NonTermViewTypeEnum; personalLearningPlanId?: number | null },
    { rejectValue: string }
>("reporting/courseFeedbackWithAnswers", async ({ studentId, familyGradeId, term, courseId, type, personalLearningPlanId }, { rejectWithValue }) => {
    try {
        const response = await getCourseFeedbackWithAnswersApi(studentId, familyGradeId, term, courseId, type, personalLearningPlanId);
        return response.data;
    } catch (err: any) {
        return rejectWithValue(err?.response?.data?.message || "Failed to load course feedback.");
    }
});

export const submitStudentFeedbackThunk = createAsyncThunk<
    void,
    FeedbackFormData,
    { rejectValue: string }
>("reporting/submitFeedback", async (feedbackData, { rejectWithValue }) => {
    try {
        await submitStudentFeedbackApi(feedbackData);
    } catch (err: any) {
        return rejectWithValue(err?.response?.data?.message || "Failed to submit feedback.");
    }
});

export const submitGradeSubhectFeedbackThunk = createAsyncThunk<
    void,
    GradeSubjectFeedbackFormData,
    { rejectValue: string }
>("reporting/submitFeedback", async (feedbackData, { rejectWithValue }) => {
    try {
        await submitGradeSubjectFeedbackApi(feedbackData);
    } catch (err: any) {
        return rejectWithValue(err?.response?.data?.message || "Failed to submit feedback.");
    }
});

export const submitSubjectFeedbackForGradeThunk = createAsyncThunk<
    void,
    SubjectFeedbackForGradeFormData,
    { rejectValue: string }
>("reporting/submitFeedback", async (feedbackData, { rejectWithValue }) => {
    try {
        await submitSubjectFeedbackForGradeApi(feedbackData);
    } catch (err: any) {
        return rejectWithValue(err?.response?.data?.message || "Failed to submit feedback.");
    }
});

export const getAllFeedbackDetailsThunk = createAsyncThunk<
    AllFeedbackDetailsApiInterface,
    { studentId: number; familyGradeId: number; term: number; type: NonTermViewTypeEnum },
    { rejectValue: string }
>("reporting/allFeedbackDetails", async ({ studentId, familyGradeId, term, type }, { rejectWithValue }) => {
    try {
        const response = await getAllFeedbackDetailsApi(studentId, familyGradeId, term, type);
        return response.data;
    } catch (err: any) {
        return rejectWithValue(err?.response?.data?.message || "Failed to load all feedback details.");
    }
});

export const getGradeSubjectFeedbackWithAnswersThunk = createAsyncThunk<
GradeSubjectFeedbackApiInterface,
    { studentId: number; familyGradeId: number; isPremiumEntitlement: boolean; },
    { rejectValue: string }
>("reporting/gradeSubjectFeedbackWithAnswers", async ({ studentId, familyGradeId, isPremiumEntitlement }, { rejectWithValue }) => {
    try {
        const response = await getGradeSubjectFeedbackWithAnswersApi(studentId, familyGradeId, isPremiumEntitlement);
        return response.data.data;
    } catch (err: any) {
        return rejectWithValue(err?.response?.data?.message || "Failed to load subject feedback.");
    }
});

export const getSubjectFeedbackForGradeWithAnswersThunk = createAsyncThunk<
SubjectFeedbackForGradeApiInterface,
    { studentId: number; familyGradeId: number; subjectId: number, personalLearningPlanId: number | null },
    { rejectValue: string }
>("reporting/subjectFeedbackForGradeWithAnswers", async ({ studentId, familyGradeId, subjectId, personalLearningPlanId }, { rejectWithValue }) => {
    try {
        const response = await getSubjectFeedbackForGradeWithAnswersApi(studentId, familyGradeId, subjectId, personalLearningPlanId);
        return response.data.data;
    } catch (err: any) {
        return rejectWithValue(err?.response?.data?.message || "Failed to load subject feedback.");
    }
});

